import { useEffect, useRef, useState } from 'react';
import type { MouseEvent } from 'react';
import type { DividendPayment, RealizedPosition } from '@tradekaro/shared';
import { Button, Card, Delta } from '../../components/UI';
import { Sparkline } from '../../components/Charts';
import { BrokerPicker } from '../../components/BrokerPicker';
import { Icon } from '../../components/Icons';
import { AllocationDonut, PerfAreaChart } from './PortfolioCharts';
import { DIVIDENDS, PORTFOLIO_HOLDINGS, PORTFOLIO_PERF, REALIZED } from './data';
import { useBreakpoint } from '../../lib/useBreakpoint';
import {
  quickAddHolding,
  searchSymbols,
  useDividends,
  usePortfolioHoldings,
  usePortfolioSummary,
  useRealized,
  type SymbolHit,
} from '../../lib/dataHooks';
import { ApiClientError } from '../../lib/api';
import { useAuth } from '../../lib/auth';

interface Props {
  onPickStock: (sym: string) => void;
}

interface EnrichedHolding {
  sym: string;
  name?: string;
  sector?: string;
  qty: number;
  avg: number;
  current: number;
  change: number;
  color: string;
  buyDate?: string;
  /** Raw ISO date string from the API (or undefined for fixtures), used to
   *  compute holding-period-from-now without relying on the formatted buyDate. */
  openedAt?: string;
  series?: number[];
  value: number;
  cost: number;
  pnl: number;
  pnlPct: number;
  dayPnl: number;
  /** Which account holds it. Absent on the static fixtures. */
  broker?: string;
  brokerName?: string;
}

type Tab = 'holdings' | 'realized' | 'dividends';

/** Format the time elapsed since `openedAt` as "Xd / X mo / X y" — short,
 *  readable, uses calendar-month math for anything > 30 days. */
function formatHoldingPeriod(openedAt: string | undefined): string {
  if (!openedAt) return '—';
  const start = new Date(openedAt).getTime();
  if (!Number.isFinite(start)) return '—';
  const days = Math.floor((Date.now() - start) / 86_400_000);
  if (days < 0) return '—';
  if (days < 30) return `${days} day${days === 1 ? '' : 's'}`;
  const months = Math.floor(days / 30.44);
  if (months < 12) return `${months} month${months === 1 ? '' : 's'}`;
  const years = months / 12;
  return `${years.toFixed(years < 2 ? 1 : 0)} years`;
}

/** Stable color per ticker — same hash as Dashboard's allocation card. */
const colorForSymbol = (sym: string): string => {
  let h = 0;
  for (let i = 0; i < sym.length; i++) h = (h * 31 + sym.charCodeAt(i)) >>> 0;
  return `oklch(var(--tk-cat-l, 0.6) var(--tk-cat-c, 0.14) ${h % 360})`;
};

export const Portfolio = ({ onPickStock }: Props) => {
  const [range, setRange] = useState<keyof typeof PORTFOLIO_PERF>('1M');
  const [tab, setTab] = useState<Tab>('holdings');
  const [expanded, setExpanded] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<keyof EnrichedHolding>('value');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const bp = useBreakpoint();
  const isMobile = bp === 'mobile';
  const isTablet = bp === 'tablet';
  const heroCols = isMobile ? '1fr' : isTablet ? '1fr 1fr' : '2fr 1fr 1fr 1fr';
  const mainCols = isMobile || isTablet ? '1fr' : '2.4fr 1fr';
  const pagePad = isMobile ? '20px 16px 60px' : '24px 32px 80px';

  const { user } = useAuth();
  // Which broker's money is on screen. 'all' adds every account together, which
  // is what the portfolio opens on; the Dashboard always shows the total.
  const [broker, setBroker] = useState('all');
  const summaryQ = usePortfolioSummary(15_000, broker);
  const holdingsQ = usePortfolioHoldings(15_000, broker);
  const realizedQ = useRealized(15_000, broker);
  const dividendsQ = useDividends();
  const live = !!summaryQ.data;

  const enriched: EnrichedHolding[] = live
    ? (holdingsQ.data?.holdings ?? []).map((h) => ({
        sym: h.sym,
        broker: h.broker,
        brokerName: h.brokerName,
        name: h.name ?? undefined,
        sector: h.sector ?? undefined,
        qty: h.qty,
        avg: h.avg,
        current: h.current,
        change: h.change,
        color: h.color ?? colorForSymbol(h.sym),
        buyDate: new Date(h.openedAt).toLocaleDateString('en-PK', {
          month: 'short',
          day: '2-digit',
          year: 'numeric',
        }),
        openedAt: h.openedAt,
        series: undefined,
        value: h.value,
        cost: h.cost,
        pnl: h.pnl,
        pnlPct: h.pnlPct,
        dayPnl: h.dayPnl,
      }))
    : PORTFOLIO_HOLDINGS.map((h) => {
        const current = h.current ?? h.avg;
        const change = h.change ?? 0;
        const value = h.qty * current;
        const cost = h.qty * h.avg;
        const pnl = value - cost;
        const pnlPct = (pnl / cost) * 100;
        const dayPnl = h.qty * current * (change / 100);
        return {
          sym: h.sym,
          name: h.name,
          sector: h.sector,
          qty: h.qty,
          avg: h.avg,
          current,
          change,
          color: h.color,
          buyDate: h.buyDate,
          series: h.series,
          value,
          cost,
          pnl,
          pnlPct,
          dayPnl,
        };
      });

  // Realized: show real DB SELL trades when authed; fixtures otherwise.
  const realizedRows: RealizedPosition[] = live
    ? (realizedQ.data?.realized ?? []).map((t) => ({
        sym: t.symbol,
        name: t.name,
        qty: t.qty,
        avgBuy: t.avgBuy,
        avgSell: t.avgSell,
        closed: new Date(t.closedAt).toLocaleDateString('en-PK', {
          month: 'short',
          day: '2-digit',
          year: 'numeric',
          timeZone: 'Asia/Karachi',
        }),
        pnl: t.pnlNet,
      }))
    : REALIZED;

  const dividendRows: DividendPayment[] = live
    ? (dividendsQ.data?.dividends ?? []).map((d) => ({
        sym: d.symbol,
        date: new Date(d.payDate).toLocaleDateString('en-PK', {
          month: 'short',
          day: '2-digit',
          year: 'numeric',
          timeZone: 'Asia/Karachi',
        }),
        perShare: Number(d.perShare),
        qty: d.qty,
        amount: Number(d.amount),
        tax: Number(d.tax ?? 0),
        zakat: Number(d.zakat ?? 0),
      }))
    : DIVIDENDS;

  const totalValue = summaryQ.data?.marketValue ?? enriched.reduce((s, h) => s + h.value, 0);
  const totalCost = summaryQ.data?.invested ?? enriched.reduce((s, h) => s + h.cost, 0);
  const totalPnl = summaryQ.data?.totalGain ?? totalValue - totalCost;
  const totalPnlPct =
    summaryQ.data?.totalGainPct ?? (totalCost > 0 ? (totalPnl / totalCost) * 100 : 0);
  const dayPnl = summaryQ.data?.dayChange ?? enriched.reduce((s, h) => s + h.dayPnl, 0);
  const cash = summaryQ.data?.cash ?? 84210;
  const realizedYTD = live ? (realizedQ.data?.totals.pnlNetYtd ?? 0) : realizedRows.reduce((s, r) => s + r.pnl, 0);
  const dividendsYTD = dividendRows.reduce((s, d) => s + d.amount, 0);

  const sorted = [...enriched].sort((a, b) => {
    const dir = sortDir === 'desc' ? -1 : 1;
    const av = a[sortBy] as number | string;
    const bv = b[sortBy] as number | string;
    return (av > bv ? 1 : -1) * dir;
  });

  // Performance chart still uses fixture series — /api/portfolio/performance endpoint
  // hasn't been built yet. Range tabs operate over fixture buckets for now.
  const perf = PORTFOLIO_PERF[range];
  const perfUp = perf.change >= 0;
  const isEmpty = live && enriched.length === 0;

  const sectorMap: Record<string, number> = {};
  enriched.forEach((h) => {
    if (h.sector) sectorMap[h.sector] = (sectorMap[h.sector] || 0) + h.value;
  });
  const sectorAlloc = Object.entries(sectorMap)
    .map(([sector, value], i) => ({
      label: sector,
      value,
      color: `oklch(calc(var(--tk-cat-l, 0.55) + ${i * 0.04}) var(--tk-cat-c, 0.14) ${30 + i * 50})`,
    }))
    .sort((a, b) => b.value - a.value);

  const stockAlloc = enriched
    .map((h) => ({ label: h.sym, value: h.value, color: h.color }))
    .sort((a, b) => b.value - a.value);

  const fmtPKR = (n: number) => 'PKR ' + n.toLocaleString('en-PK', { maximumFractionDigits: 0 });

  return (
    <div
      style={{ maxWidth: 1440, margin: '0 auto', padding: pagePad }}
      className="tk-fade-in"
    >
      <div
        style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'stretch' : 'flex-end',
          justifyContent: 'space-between',
          marginBottom: 24,
          gap: 16,
        }}
      >
        <div>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: 'var(--tk-ink-3)',
              fontFamily: 'var(--tk-mono)',
              marginBottom: 6,
            }}
          >
            Portfolio · Live valuation
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
            <h1 style={{ margin: 0, fontSize: 32, fontWeight: 700, letterSpacing: '-0.025em' }}>
              Your investments
            </h1>
            <BrokerPicker value={broker} onChange={setBroker} />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button variant="ghost" size="md" icon="arrowDown">
            Export
          </Button>
          <Button variant="ghost" size="md" icon="plus" onClick={() => setShowQuickAdd(true)}>
            Quick add
          </Button>
          <Button variant="primary" size="md">
            Deposit funds
          </Button>
        </div>
      </div>

      {showQuickAdd && (
        <QuickAddModal
          onClose={() => setShowQuickAdd(false)}
          onSubmitted={async () => {
            await Promise.all([
              summaryQ.refetch(),
              holdingsQ.refetch(),
              realizedQ.refetch(),
              dividendsQ.refetch(),
            ]);
            setShowQuickAdd(false);
          }}
        />
      )}

      <div
        style={{ display: 'grid', gridTemplateColumns: heroCols, gap: 16, marginBottom: 20 }}
      >
        <Card
          padding={24}
          style={{
            background: 'linear-gradient(135deg, var(--tk-accent), oklch(0.58 0.16 22))',
            color: 'var(--tk-accent-ink)',
            border: 'none',
          }}
        >
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              opacity: 0.75,
              fontFamily: 'var(--tk-mono)',
            }}
          >
            Total portfolio value
          </div>
          <div
            style={{
              fontSize: 38,
              fontWeight: 700,
              letterSpacing: '-0.025em',
              marginTop: 8,
              lineHeight: 1,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {fmtPKR(totalValue + cash)}
          </div>
          <div style={{ display: 'flex', gap: 18, marginTop: 14, fontSize: 12.5 }}>
            <div title="What the shares are worth now; cost basis is in the table below">
              {/* This said "Invested" while showing market value, so it always
                  equalled the headline total and told you nothing. */}
              <div style={{ opacity: 0.7, fontSize: 11 }}>Holdings</div>
              <div className="mono" style={{ fontWeight: 600, marginTop: 2 }}>
                {fmtPKR(totalValue)}
              </div>
            </div>
            <div
              title={
                summaryQ.data
                  ? [
                      summaryQ.data.cashMirrored > 0 ? `${fmtPKR(summaryQ.data.cashMirrored)} buying power at the broker` : null,
                      summaryQ.data.settledCash > summaryQ.data.cashMirrored
                        ? `${fmtPKR(summaryQ.data.settledCash)} settled balance — still counts today's unsettled buys, so it is not added to the total`
                        : null,
                      summaryQ.data.cashLedger !== 0 ? `${fmtPKR(summaryQ.data.cashLedger)} from your own ledger` : null,
                      summaryQ.data.buyingPower > 0 ? `Buying power ${fmtPKR(summaryQ.data.buyingPower)}` : null,
                    ]
                      .filter(Boolean)
                      .join(' · ')
                  : undefined
              }
              style={{ cursor: summaryQ.data ? 'help' : undefined }}
            >
              <div style={{ opacity: 0.7, fontSize: 11 }}>Cash</div>
              <div className="mono" style={{ fontWeight: 600, marginTop: 2 }}>
                {fmtPKR(cash)}
              </div>
              {/* Settled cash and what you can actually trade with differ once a
                  broker reserves against open buys. */}
              {summaryQ.data && summaryQ.data.settledCash > summaryQ.data.cashMirrored && (
                <div className="mono" style={{ opacity: 0.6, fontSize: 10.5, marginTop: 1 }}>
                  {fmtPKR(summaryQ.data.settledCash)} unsettled
                </div>
              )}
            </div>
            <div>
              <div style={{ opacity: 0.7, fontSize: 11 }}>Today</div>
              <div className="mono" style={{ fontWeight: 600, marginTop: 2 }}>
                {dayPnl >= 0 ? '+' : ''}
                {fmtPKR(dayPnl)}
              </div>
            </div>
          </div>
        </Card>
        <KpiCard
          label="Unrealized P&L"
          value={fmtPKR(totalPnl)}
          delta={totalCost > 0 ? totalPnlPct : undefined}
          highlight
        />
        <KpiCard
          label="Realized YTD"
          value={fmtPKR(realizedYTD)}
          sub="After trading charges, before CGT"
          up={realizedYTD >= 0}
        />
        <KpiCard
          label="Dividends YTD"
          value={fmtPKR(dividendsYTD)}
          sub={`${dividendRows.length} payment${dividendRows.length === 1 ? '' : 's'}`}
          up
        />
      </div>

      <Card padding={0} style={{ marginBottom: 20 }}>
        <div
          style={{
            padding: '20px 24px 8px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
          }}
        >
          <div>
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>Performance</h3>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginTop: 6 }}>
              <span
                className="mono"
                style={{
                  fontSize: 24,
                  fontWeight: 700,
                  letterSpacing: '-0.02em',
                  color: perfUp ? 'var(--tk-up)' : 'var(--tk-down)',
                }}
              >
                {perfUp ? '+' : ''}
                {perf.change.toFixed(2)}%
              </span>
              <span style={{ fontSize: 13, color: 'var(--tk-ink-3)' }}>
                vs KSE-100 {(perf.change - 1.4).toFixed(2)}% · {range}
              </span>
            </div>
          </div>
          <div
            style={{
              display: 'flex',
              gap: 4,
              padding: 4,
              background: 'var(--tk-surface-2)',
              borderRadius: 'var(--tk-radius-sm)',
              border: '1px solid var(--tk-line)',
            }}
          >
            {(Object.keys(PORTFOLIO_PERF) as (keyof typeof PORTFOLIO_PERF)[]).map((r) => (
              <button
                key={r}
                onClick={() => setRange(r)}
                style={{
                  padding: '6px 12px',
                  fontSize: 12,
                  fontWeight: 600,
                  fontFamily: 'var(--tk-mono)',
                  color: range === r ? 'var(--tk-ink)' : 'var(--tk-ink-3)',
                  background: range === r ? 'var(--tk-surface)' : 'transparent',
                  border: 'none',
                  borderRadius: 7,
                  cursor: 'pointer',
                  boxShadow: range === r ? 'var(--tk-shadow-sm)' : 'none',
                }}
              >
                {r}
              </button>
            ))}
          </div>
        </div>
        <PerfAreaChart series={perf.series} height={280} up={perfUp} />
      </Card>

      <div style={{ display: 'grid', gridTemplateColumns: mainCols, gap: 20 }}>
        <div style={{ minWidth: 0 }}>
          <Card padding={0}>
            <div
              style={{
                display: 'flex',
                gap: 4,
                padding: '14px 20px 0',
                borderBottom: '1px solid var(--tk-line-2)',
              }}
            >
              {(
                [
                  { id: 'holdings', label: 'Holdings', count: enriched.length },
                  { id: 'realized', label: 'Realized', count: realizedRows.length },
                  { id: 'dividends', label: 'Dividends', count: dividendRows.length },
                ] as const
              ).map((t) => (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  style={{
                    padding: '10px 14px',
                    fontSize: 13,
                    fontWeight: 600,
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    color: tab === t.id ? 'var(--tk-ink)' : 'var(--tk-ink-3)',
                    borderBottom:
                      tab === t.id ? '2px solid var(--tk-accent)' : '2px solid transparent',
                    marginBottom: -1,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  {t.label}
                  <span
                    style={{
                      fontSize: 10.5,
                      fontFamily: 'var(--tk-mono)',
                      padding: '1px 6px',
                      borderRadius: 3,
                      background: 'var(--tk-surface-2)',
                      color: 'var(--tk-ink-3)',
                    }}
                  >
                    {t.count}
                  </span>
                </button>
              ))}
            </div>

            {tab === 'holdings' &&
              (isEmpty ? (
                <EmptyTabState
                  icon="briefcase"
                  title="No holdings yet"
                  body="Buy a stock from the Markets page, or import a past position via Quick add."
                  cta={
                    <Button
                      variant="primary"
                      size="sm"
                      icon="plus"
                      onClick={() => setShowQuickAdd(true)}
                    >
                      Quick add
                    </Button>
                  }
                />
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <HoldingsTable
                    showBroker={broker === 'all'}
                    holdings={sorted}
                    sortBy={sortBy}
                    sortDir={sortDir}
                    onSort={(k) => {
                      if (k === sortBy) {
                        setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
                      } else {
                        setSortBy(k);
                        setSortDir('desc');
                      }
                    }}
                    expanded={expanded}
                    onToggle={(s) => setExpanded((e) => (e === s ? null : s))}
                    onPickStock={onPickStock}
                  />
                </div>
              ))}
            {tab === 'realized' && !!realizedQ.data?.issues.length && (
              <div role="status" style={{ padding: 12, color: 'var(--tk-down)' }}>Incomplete accounting: {realizedQ.data.issues.join('; ')}. Figures include known acquisition costs only.</div>
            )}
            {tab === 'realized' &&
              (live && realizedRows.length === 0 ? (
                <EmptyTabState
                  icon="arrowUp"
                  title="No realized trades yet"
                  body="Sell a position to record realized P&L. Closed trips will appear here."
                />
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <RealizedTable rows={realizedRows} onPickStock={onPickStock} />
                </div>
              ))}
            {tab === 'dividends' &&
              (live && dividendRows.length === 0 ? (
                <EmptyTabState
                  icon="star"
                  title="No dividends recorded"
                  body="When companies you hold pay dividends, the credits will show up here."
                />
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <DividendsTable rows={dividendRows} onPickStock={onPickStock} />
                </div>
              ))}
          </Card>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {isEmpty ? (
            <Card padding={24}>
              <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600, marginBottom: 8 }}>
                Allocation & risk
              </h3>
              <p
                style={{
                  margin: 0,
                  fontSize: 12.5,
                  color: 'var(--tk-ink-3)',
                  lineHeight: 1.5,
                }}
              >
                Allocation breakdowns and the risk profile populate once you hold at least one
                position.
              </p>
            </Card>
          ) : (
            <>
              <PortfolioAllocationCard title="By sector" items={sectorAlloc} total={totalValue} />
              <PortfolioAllocationCard
                title="By stock"
                items={stockAlloc}
                total={totalValue}
                compact
              />
              <RiskCard holdings={enriched} totalValue={totalValue} />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const EmptyTabState = ({
  icon,
  title,
  body,
  cta,
}: {
  icon: 'briefcase' | 'arrowUp' | 'star';
  title: string;
  body: string;
  cta?: React.ReactNode;
}) => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '60px 24px',
      gap: 14,
      textAlign: 'center',
    }}
  >
    <div
      style={{
        width: 56,
        height: 56,
        borderRadius: 999,
        background: 'var(--tk-surface-2)',
        border: '1px dashed var(--tk-line)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'var(--tk-ink-3)',
      }}
    >
      <Icon name={icon} size={22} stroke={1.4} />
    </div>
    <div>
      <div
        style={{ fontSize: 14, fontWeight: 700, color: 'var(--tk-ink)', marginBottom: 4 }}
      >
        {title}
      </div>
      <div style={{ fontSize: 12.5, color: 'var(--tk-ink-3)', maxWidth: 360, lineHeight: 1.5 }}>
        {body}
      </div>
    </div>
    {cta}
  </div>
);

const QuickAddModal = ({
  onClose,
  onSubmitted,
}: {
  onClose: () => void;
  onSubmitted: () => Promise<void>;
}) => {
  const [picked, setPicked] = useState<SymbolHit | null>(null);
  const [qty, setQty] = useState('');
  const [avgPrice, setAvgPrice] = useState('');
  const [buyDate, setBuyDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    setError(null);
    if (!picked) {
      setError('Pick a symbol from the catalogue');
      return;
    }
    const q = Number(qty);
    const p = Number(avgPrice);
    if (!q || q <= 0 || !p || p <= 0) {
      setError('Quantity and average price are required');
      return;
    }
    setSubmitting(true);
    try {
      await quickAddHolding({
        symbol: picked.sym,
        qty: q,
        avgPrice: p,
        buyDate: new Date(buyDate).toISOString(),
      });
      await onSubmitted();
    } catch (e) {
      if (e instanceof ApiClientError) setError(e.message);
      else setError((e as Error).message || 'Failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 100,
        background: 'color-mix(in oklch, black 50%, transparent)',
        backdropFilter: 'blur(6px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: 480,
          background: 'var(--tk-surface)',
          borderRadius: 'var(--tk-radius-lg)',
          boxShadow: 'var(--tk-shadow-lg)',
          padding: 24,
        }}
      >
        <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, letterSpacing: '-0.02em' }}>
          Quick add holding
        </h3>
        <p
          style={{
            margin: '4px 0 18px',
            fontSize: 12.5,
            color: 'var(--tk-ink-3)',
            lineHeight: 1.5,
          }}
        >
          Record a position you already held. Does not debit cash — for that, use the Buy ticket on
          the stock detail page.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <QField label="Symbol">
            <SymbolSearchInput value={picked} onChange={setPicked} />
          </QField>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <QField label="Quantity (shares)">
              <QInput
                value={qty}
                onChange={(e) => setQty(e.target.value.replace(/[^\d]/g, ''))}
                placeholder="250"
                inputMode="numeric"
              />
            </QField>
            <QField label="Avg price (PKR)">
              <QInput
                value={avgPrice}
                onChange={(e) => setAvgPrice(e.target.value.replace(/[^\d.]/g, ''))}
                placeholder="281.40"
                inputMode="decimal"
              />
            </QField>
          </div>
          <QField label="Buy date">
            <QInput type="date" value={buyDate} onChange={(e) => setBuyDate(e.target.value)} />
          </QField>
          {error && (
            <div
              style={{
                padding: '8px 12px',
                borderRadius: 'var(--tk-radius-sm)',
                background: 'var(--tk-down-soft)',
                color: 'var(--tk-down)',
                fontSize: 12.5,
                fontWeight: 600,
              }}
            >
              {error}
            </div>
          )}
          <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
            <Button
              variant="ghost"
              size="lg"
              onClick={onClose}
              style={{ flex: 1, justifyContent: 'center' }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="lg"
              onClick={handleSubmit}
              style={{
                flex: 2,
                justifyContent: 'center',
                opacity: submitting ? 0.6 : 1,
                pointerEvents: submitting ? 'none' : 'auto',
              }}
            >
              {submitting ? 'Adding…' : 'Add to portfolio'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

const QField = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <label style={{ display: 'block' }}>
    <span
      style={{
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: 'var(--tk-ink-3)',
        fontFamily: 'var(--tk-mono)',
        marginBottom: 6,
        display: 'block',
      }}
    >
      {label}
    </span>
    {children}
  </label>
);

const QInput = (props: React.InputHTMLAttributes<HTMLInputElement>) => (
  <input
    {...props}
    style={{
      width: '100%',
      padding: '11px 14px',
      background: 'var(--tk-surface-2)',
      border: '1px solid var(--tk-line)',
      borderRadius: 'var(--tk-radius-sm)',
      fontSize: 14,
      fontFamily: 'var(--tk-sans)',
      color: 'var(--tk-ink)',
      outline: 'none',
    }}
  />
);

const SymbolSearchInput = ({
  value,
  onChange,
}: {
  value: SymbolHit | null;
  onChange: (s: SymbolHit | null) => void;
}) => {
  const [query, setQuery] = useState(value?.sym ?? '');
  const [results, setResults] = useState<SymbolHit[]>([]);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onDoc = (e: globalThis.MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  useEffect(() => {
    const trimmed = query.trim();
    if (!trimmed || (value && value.sym === trimmed)) {
      setResults([]);
      return;
    }
    setLoading(true);
    setError(null);
    const handle = setTimeout(async () => {
      try {
        const r = await searchSymbols(trimmed, 10);
        setResults(r.results);
        setHighlight(0);
      } catch (e) {
        setError(e instanceof ApiClientError ? e.message : 'Search failed');
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 200);
    return () => clearTimeout(handle);
  }, [query, value]);

  const select = (hit: SymbolHit) => {
    onChange(hit);
    setQuery(hit.sym);
    setOpen(false);
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <QInput
        value={query}
        placeholder="Type symbol or company name (e.g. ENGROH or Habib)"
        onChange={(e) => {
          setQuery(e.target.value.toUpperCase());
          if (value) onChange(null);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (!open || results.length === 0) return;
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHighlight((h) => Math.min(results.length - 1, h + 1));
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlight((h) => Math.max(0, h - 1));
          } else if (e.key === 'Enter') {
            e.preventDefault();
            select(results[highlight]);
          } else if (e.key === 'Escape') {
            setOpen(false);
          }
        }}
      />
      {value && (
        <div
          style={{
            marginTop: 6,
            fontSize: 11.5,
            color: 'var(--tk-ink-3)',
            display: 'flex',
            gap: 8,
            alignItems: 'center',
          }}
        >
          <span
            className="mono"
            style={{
              padding: '2px 7px',
              borderRadius: 4,
              background: 'var(--tk-up-soft)',
              color: 'var(--tk-up)',
              fontSize: 10.5,
              fontWeight: 700,
              letterSpacing: '0.06em',
            }}
          >
            ✓ {value.market}
          </span>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {value.name}
          </span>
        </div>
      )}
      {open && (results.length > 0 || loading || error) && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            right: 0,
            background: 'var(--tk-surface)',
            border: '1px solid var(--tk-line)',
            borderRadius: 'var(--tk-radius-md)',
            boxShadow: 'var(--tk-shadow-lg)',
            maxHeight: 320,
            overflowY: 'auto',
            zIndex: 200,
          }}
        >
          {loading && results.length === 0 && (
            <div style={{ padding: 14, fontSize: 12.5, color: 'var(--tk-ink-3)' }}>Searching…</div>
          )}
          {error && (
            <div style={{ padding: 14, fontSize: 12.5, color: 'var(--tk-down)' }}>{error}</div>
          )}
          {results.map((r, i) => (
            <div
              key={`${r.sym}-${r.market}`}
              onMouseDown={(e) => {
                e.preventDefault();
                select(r);
              }}
              onMouseEnter={() => setHighlight(i)}
              style={{
                display: 'grid',
                gridTemplateColumns: '70px 1fr auto auto',
                alignItems: 'center',
                gap: 10,
                padding: '10px 12px',
                cursor: 'pointer',
                background: i === highlight ? 'var(--tk-surface-2)' : 'transparent',
              }}
            >
              <span
                className="mono"
                style={{
                  fontSize: 12.5,
                  fontWeight: 700,
                  color: 'var(--tk-ink)',
                  letterSpacing: '0.01em',
                }}
              >
                {r.sym}
              </span>
              <span
                style={{
                  fontSize: 12,
                  color: 'var(--tk-ink-2)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {r.name}
              </span>
              <span
                style={{
                  fontSize: 9.5,
                  color: 'var(--tk-ink-3)',
                  fontFamily: 'var(--tk-mono)',
                  letterSpacing: '0.06em',
                }}
              >
                {r.market}
              </span>
              <span
                className="mono"
                style={{
                  fontSize: 11.5,
                  fontWeight: 600,
                  color: 'var(--tk-ink-3)',
                  textAlign: 'right',
                  minWidth: 60,
                }}
              >
                {r.lastPrice ? Number(r.lastPrice).toFixed(2) : '—'}
              </span>
            </div>
          ))}
          {!loading && !error && query.trim() && results.length === 0 && (
            <div style={{ padding: 14, fontSize: 12.5, color: 'var(--tk-ink-3)' }}>
              No symbols matching “{query}”
            </div>
          )}
        </div>
      )}
    </div>
  );
};

interface KpiProps {
  label: string;
  value: string;
  delta?: number;
  sub?: string;
  highlight?: boolean;
  up?: boolean;
}

const KpiCard = ({ label, value, delta, sub, highlight, up }: KpiProps) => (
  <Card padding={20} style={highlight ? { borderColor: 'var(--tk-accent)' } : {}}>
    <div
      style={{
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: 'var(--tk-ink-3)',
        fontFamily: 'var(--tk-mono)',
      }}
    >
      {label}
    </div>
    <div
      className="mono"
      style={{
        fontSize: 24,
        fontWeight: 700,
        letterSpacing: '-0.02em',
        marginTop: 8,
        color:
          (up !== undefined ? up : delta === undefined ? true : delta >= 0)
            ? 'var(--tk-up)'
            : 'var(--tk-down)',
      }}
    >
      {value}
    </div>
    {delta !== undefined && (
      <div
        style={{
          fontSize: 12,
          fontWeight: 600,
          marginTop: 4,
          fontFamily: 'var(--tk-mono)',
          color: delta >= 0 ? 'var(--tk-up)' : 'var(--tk-down)',
        }}
      >
        {delta >= 0 ? '+' : ''}
        {delta.toFixed(2)}%
      </div>
    )}
    {sub && <div style={{ fontSize: 11.5, color: 'var(--tk-ink-3)', marginTop: 4 }}>{sub}</div>}
  </Card>
);

interface HoldingsTableProps {
  holdings: EnrichedHolding[];
  sortBy: keyof EnrichedHolding;
  sortDir: 'asc' | 'desc';
  onSort: (k: keyof EnrichedHolding) => void;
  expanded: string | null;
  onToggle: (sym: string) => void;
  onPickStock: (sym: string) => void;
}

const HoldingsTable = ({
  holdings,
  sortBy,
  sortDir,
  onSort,
  expanded,
  onToggle,
  onPickStock,
  showBroker = false,
}: HoldingsTableProps & { showBroker?: boolean }) => {
  const headers: { id: keyof EnrichedHolding | 'spark' | 'actions'; label: string; align: 'left' | 'right' | 'center'; sortable?: boolean }[] = [
    { id: 'sym', label: 'Symbol', align: 'left', sortable: true },
    { id: 'qty', label: 'Qty', align: 'right', sortable: true },
    { id: 'avg', label: 'Avg cost', align: 'right', sortable: true },
    { id: 'current', label: 'Last', align: 'right', sortable: true },
    { id: 'value', label: 'Market value', align: 'right', sortable: true },
    { id: 'pnl', label: 'Unrealized P&L', align: 'right', sortable: true },
    { id: 'dayPnl', label: 'Today', align: 'right', sortable: true },
    { id: 'spark', label: '30d', align: 'center' },
    { id: 'actions', label: '', align: 'right' },
  ];

  return (
    <div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1.4fr 0.7fr 0.9fr 0.9fr 1.1fr 1.2fr 1fr 0.7fr 0.4fr',
          minWidth: 880,
          padding: '12px 20px',
          fontSize: 10.5,
          fontWeight: 700,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: 'var(--tk-ink-3)',
          fontFamily: 'var(--tk-mono)',
          borderBottom: '1px solid var(--tk-line-2)',
          background: 'var(--tk-surface-2)',
        }}
      >
        {headers.map((h) => (
          <span
            key={h.id as string}
            onClick={() => h.sortable && onSort(h.id as keyof EnrichedHolding)}
            style={{
              textAlign: h.align,
              cursor: h.sortable ? 'pointer' : 'default',
              userSelect: 'none',
              color: sortBy === (h.id as keyof EnrichedHolding) ? 'var(--tk-ink)' : undefined,
            }}
          >
            {h.label}
            {sortBy === (h.id as keyof EnrichedHolding) && (
              <span style={{ marginLeft: 4 }}>{sortDir === 'desc' ? '↓' : '↑'}</span>
            )}
          </span>
        ))}
      </div>

      {holdings.map((h) => {
        // The same stock at two brokers is two rows, so the row identity has to
        // carry the broker or they collide and expand together.
        const rowKey = h.broker ? `${h.sym}@${h.broker}` : h.sym;
        const isExp = expanded === rowKey;
        const allocPct = (h.value / holdings.reduce((s, x) => s + x.value, 0)) * 100;
        return (
          <div key={rowKey}>
            <div
              onClick={() => onToggle(rowKey)}
              style={{
                display: 'grid',
                gridTemplateColumns: '1.4fr 0.7fr 0.9fr 0.9fr 1.1fr 1.2fr 1fr 0.7fr 0.4fr',
                minWidth: 880,
                padding: '14px 20px',
                alignItems: 'center',
                borderBottom: '1px solid var(--tk-line-2)',
                cursor: 'pointer',
                background: isExp ? 'var(--tk-surface-2)' : 'transparent',
                transition: 'background 0.15s var(--tk-ease)',
                fontVariantNumeric: 'tabular-nums',
              }}
              onMouseEnter={(e) => {
                if (!isExp) e.currentTarget.style.background = 'var(--tk-surface-2)';
              }}
              onMouseLeave={(e) => {
                if (!isExp) e.currentTarget.style.background = 'transparent';
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                <span
                  style={{
                    width: 6,
                    height: 32,
                    borderRadius: 3,
                    background: h.color,
                    flexShrink: 0,
                  }}
                />
                <div style={{ minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontWeight: 700, fontSize: 13, fontFamily: 'var(--tk-mono)' }}>{h.sym}</span>
                    {/* Only while looking at everything: with one broker chosen
                        the label is on the selector and repeating it is noise. */}
                    {showBroker && h.brokerName && (
                      <span
                        title={`Held at ${h.brokerName}`}
                        style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: '0.06em', padding: '1px 5px', borderRadius: 3, background: 'var(--tk-surface-2)', color: 'var(--tk-ink-3)', fontFamily: 'var(--tk-mono)', whiteSpace: 'nowrap' }}
                      >
                        {h.brokerName.split(' ')[0].toUpperCase()}
                      </span>
                    )}
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: 'var(--tk-ink-3)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {h.sector}
                  </div>
                </div>
              </div>
              <span className="mono" style={{ textAlign: 'right', fontSize: 13 }}>
                {h.qty.toLocaleString()}
              </span>
              <span
                className="mono"
                style={{ textAlign: 'right', fontSize: 13, color: 'var(--tk-ink-2)' }}
              >
                {h.avg.toFixed(2)}
              </span>
              <span
                className="mono"
                style={{ textAlign: 'right', fontSize: 13, fontWeight: 600 }}
              >
                {h.current.toFixed(2)}
              </span>
              <span
                className="mono"
                style={{ textAlign: 'right', fontSize: 13.5, fontWeight: 600 }}
              >
                {h.value.toLocaleString('en-PK', { maximumFractionDigits: 0 })}
              </span>
              <div style={{ textAlign: 'right' }}>
                <div
                  className="mono"
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: h.pnl >= 0 ? 'var(--tk-up)' : 'var(--tk-down)',
                  }}
                >
                  {h.pnl >= 0 ? '+' : ''}
                  {h.pnl.toLocaleString('en-PK', { maximumFractionDigits: 0 })}
                </div>
                <div
                  style={{
                    fontSize: 11,
                    fontFamily: 'var(--tk-mono)',
                    fontWeight: 600,
                    color: h.pnl >= 0 ? 'var(--tk-up)' : 'var(--tk-down)',
                  }}
                >
                  {h.pnl >= 0 ? '+' : ''}
                  {h.pnlPct.toFixed(2)}%
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <Delta value={h.change} size="sm" />
              </div>
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                {h.series && (
                  <Sparkline data={h.series} width={70} height={24} up={h.change >= 0} fill />
                )}
              </div>
              <div style={{ textAlign: 'right', color: 'var(--tk-ink-3)' }}>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  style={{
                    transform: isExp ? 'rotate(180deg)' : 'rotate(0)',
                    transition: 'transform 0.18s',
                  }}
                >
                  <path d="m6 9 6 6 6-6" />
                </svg>
              </div>
            </div>

            {isExp && (
              <div
                className="tk-fade-in"
                style={{
                  padding: '20px 24px 24px',
                  background: 'var(--tk-surface-2)',
                  borderBottom: '1px solid var(--tk-line)',
                }}
              >
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(5, 1fr) auto',
                    gap: 24,
                    alignItems: 'center',
                  }}
                >
                  <ExpStat
                    label="Cost basis"
                    value={`PKR ${h.cost.toLocaleString('en-PK', { maximumFractionDigits: 0 })}`}
                  />
                  <ExpStat label="Acquired" value={h.buyDate ?? '—'} />
                  <ExpStat label="Holding period" value={formatHoldingPeriod(h.openedAt)} />
                  <ExpStat label="Allocation" value={`${allocPct.toFixed(1)}%`} />
                  {/* "Days held in profit" needs per-day mark-to-market history per
                      symbol — not yet computed. Showing a dash is honest until
                      we wire DPS EOD bars + closing-price-vs-cost per day. */}
                  <ExpStat label="Days held in profit" value="—" />
                  <div style={{ display: 'flex', gap: 8 }}>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e: MouseEvent<HTMLButtonElement>) => {
                        e.stopPropagation();
                        onPickStock(h.sym);
                      }}
                    >
                      Open detail →
                    </Button>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={(e: MouseEvent<HTMLButtonElement>) => {
                        e.stopPropagation();
                        onPickStock(h.sym);
                      }}
                    >
                      Trade
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const ExpStat = ({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: 'up' | 'down';
}) => (
  <div>
    <div
      style={{
        fontSize: 10.5,
        fontWeight: 700,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: 'var(--tk-ink-3)',
      }}
    >
      {label}
    </div>
    <div
      className="mono"
      style={{
        fontSize: 14,
        fontWeight: 600,
        marginTop: 4,
        color: tone === 'up' ? 'var(--tk-up)' : 'var(--tk-ink)',
      }}
    >
      {value}
    </div>
  </div>
);

const RealizedTable = ({
  rows,
  onPickStock,
}: {
  rows: RealizedPosition[];
  onPickStock: (sym: string) => void;
}) => {
  const total = rows.reduce((s, r) => s + r.pnl, 0);
  return (
    <div>
      <div
        style={{
          padding: '14px 20px',
          borderBottom: '1px solid var(--tk-line-2)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          background: 'var(--tk-surface-2)',
        }}
      >
        <span style={{ fontSize: 12.5, color: 'var(--tk-ink-2)' }}>Realized YTD</span>
        <span
          className="mono"
          style={{
            fontSize: 16,
            fontWeight: 700,
            color: total >= 0 ? 'var(--tk-up)' : 'var(--tk-down)',
          }}
        >
          {total >= 0 ? '+' : ''}PKR {total.toLocaleString()}
        </span>
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 0.7fr 0.9fr 0.9fr 1fr 1fr',
          padding: '12px 20px',
          fontSize: 10.5,
          fontWeight: 700,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: 'var(--tk-ink-3)',
          fontFamily: 'var(--tk-mono)',
          borderBottom: '1px solid var(--tk-line-2)',
        }}
      >
        <span>Symbol</span>
        <span style={{ textAlign: 'right' }}>Qty</span>
        <span style={{ textAlign: 'right' }}>Avg buy</span>
        <span style={{ textAlign: 'right' }}>Avg sell</span>
        <span style={{ textAlign: 'right' }}>Closed</span>
        <span style={{ textAlign: 'right' }}>Realized P&L</span>
      </div>
      {rows.map((r, i) => (
        <div
          key={i}
          onClick={() => onPickStock(r.sym)}
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 0.7fr 0.9fr 0.9fr 1fr 1fr',
            padding: '13px 20px',
            fontFamily: 'var(--tk-mono)',
            fontSize: 13,
            borderBottom: i < rows.length - 1 ? '1px solid var(--tk-line-2)' : 'none',
            cursor: 'pointer',
            alignItems: 'center',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          <div>
            <div style={{ fontWeight: 700, color: 'var(--tk-ink)' }}>{r.sym}</div>
            <div
              style={{
                fontSize: 10.5,
                color: 'var(--tk-ink-3)',
                fontFamily: 'var(--tk-sans)',
                marginTop: 1,
              }}
            >
              {r.name}
            </div>
          </div>
          <span style={{ textAlign: 'right' }}>{r.qty}</span>
          <span style={{ textAlign: 'right', color: 'var(--tk-ink-2)' }}>{r.avgBuy.toFixed(2)}</span>
          <span style={{ textAlign: 'right' }}>{r.avgSell.toFixed(2)}</span>
          <span
            style={{
              textAlign: 'right',
              fontFamily: 'var(--tk-sans)',
              color: 'var(--tk-ink-3)',
              fontSize: 12,
            }}
          >
            {r.closed}
          </span>
          <span
            style={{
              textAlign: 'right',
              fontWeight: 700,
              color: r.pnl >= 0 ? 'var(--tk-up)' : 'var(--tk-down)',
            }}
          >
            {r.pnl >= 0 ? '+' : ''}
            {r.pnl.toLocaleString()}
          </span>
        </div>
      ))}
    </div>
  );
};

const DividendsTable = ({
  rows,
  onPickStock,
}: {
  rows: DividendPayment[];
  onPickStock: (sym: string) => void;
}) => {
  const total = rows.reduce((s, r) => s + r.amount, 0);
  const totalTax = rows.reduce((s, r) => s + (r.tax ?? 0), 0);
  const totalZakat = rows.reduce((s, r) => s + (r.zakat ?? 0), 0);
  const money = (n: number) => n.toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const cols = '0.9fr 1.1fr 0.9fr 0.9fr 1fr 1fr 1.2fr';
  return (
    <div>
      <div
        style={{
          padding: '14px 20px',
          borderBottom: '1px solid var(--tk-line-2)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          background: 'var(--tk-surface-2)',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <span style={{ fontSize: 12.5, color: 'var(--tk-ink-2)' }}>
          Dividends received (net)
          <span style={{ marginLeft: 10, fontSize: 11.5, color: 'var(--tk-ink-3)', fontFamily: 'var(--tk-mono)' }}>
            tax withheld {money(totalTax)} · zakat {money(totalZakat)}
          </span>
        </span>
        <span className="mono" style={{ fontSize: 16, fontWeight: 700, color: 'var(--tk-up)' }}>
          PKR {money(total)}
        </span>
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: cols,
          padding: '12px 20px',
          fontSize: 10.5,
          fontWeight: 700,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: 'var(--tk-ink-3)',
          fontFamily: 'var(--tk-mono)',
          borderBottom: '1px solid var(--tk-line-2)',
        }}
      >
        <span>Symbol</span>
        <span>Pay date</span>
        <span style={{ textAlign: 'right' }}>Net / share</span>
        <span style={{ textAlign: 'right' }}>Qty</span>
        <span style={{ textAlign: 'right' }}>Tax</span>
        <span style={{ textAlign: 'right' }}>Zakat</span>
        <span style={{ textAlign: 'right' }}>Net amount</span>
      </div>
      {rows.map((r, i) => (
        <div
          key={i}
          onClick={() => onPickStock(r.sym)}
          style={{
            display: 'grid',
            gridTemplateColumns: cols,
            padding: '13px 20px',
            fontFamily: 'var(--tk-mono)',
            fontSize: 13,
            borderBottom: i < rows.length - 1 ? '1px solid var(--tk-line-2)' : 'none',
            cursor: 'pointer',
            alignItems: 'center',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          <span style={{ fontWeight: 700 }}>{r.sym}</span>
          <span
            style={{ fontFamily: 'var(--tk-sans)', color: 'var(--tk-ink-3)', fontSize: 12 }}
          >
            {r.date}
          </span>
          <span style={{ textAlign: 'right' }}>{r.qty > 0 ? r.perShare.toFixed(2) : '—'}</span>
          <span style={{ textAlign: 'right' }}>{r.qty > 0 ? r.qty.toLocaleString() : '—'}</span>
          <span style={{ textAlign: 'right', color: 'var(--tk-ink-2)' }}>{money(r.tax ?? 0)}</span>
          <span style={{ textAlign: 'right', color: 'var(--tk-ink-2)' }}>{money(r.zakat ?? 0)}</span>
          <span style={{ textAlign: 'right', fontWeight: 700, color: 'var(--tk-up)' }}>
            +{money(r.amount)}
          </span>
        </div>
      ))}
    </div>
  );
};

interface AllocationCardProps {
  title: string;
  items: { label: string; value: number; color: string }[];
  total: number;
  compact?: boolean;
}

const PortfolioAllocationCard = ({ title, items, total, compact }: AllocationCardProps) => (
  <Card padding={20}>
    <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600, marginBottom: 16 }}>{title}</h3>
    <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 14 }}>
      <AllocationDonut items={items} size={compact ? 160 : 180} thickness={compact ? 22 : 26} />
    </div>
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {items.slice(0, compact ? 5 : 6).map((it, i) => (
        <div
          key={i}
          style={{
            display: 'grid',
            gridTemplateColumns: '12px 1fr auto',
            gap: 10,
            alignItems: 'center',
          }}
        >
          <span style={{ width: 8, height: 8, borderRadius: 2, background: it.color }} />
          <span style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--tk-ink-2)' }}>
            {it.label}
          </span>
          <span className="mono" style={{ fontSize: 12, fontWeight: 600, color: 'var(--tk-ink)' }}>
            {((it.value / total) * 100).toFixed(1)}%
          </span>
        </div>
      ))}
    </div>
  </Card>
);

const RiskCard = ({
  holdings,
  totalValue,
}: {
  holdings: EnrichedHolding[];
  totalValue: number;
}) => {
  const top = holdings.slice().sort((a, b) => b.value - a.value)[0];
  const concentration = top ? (top.value / totalValue) * 100 : 0;

  // Sector spread: count distinct non-empty sectors across current holdings.
  // Symbols with null sector are bucketed as "Unknown" and counted once.
  const sectorCounts: Record<string, number> = {};
  for (const h of holdings) {
    const s = h.sector?.trim() || 'Unknown';
    sectorCounts[s] = (sectorCounts[s] ?? 0) + 1;
  }
  const sectorCount = Object.keys(sectorCounts).length;
  const sectorSpreadTone =
    sectorCount >= 5 ? 'up' : sectorCount >= 3 ? undefined : 'down';
  const sectorSpreadSub =
    sectorCount >= 5
      ? 'Well diversified'
      : sectorCount >= 3
        ? 'Moderately diversified'
        : 'Concentrated — add more sectors';

  return (
    <Card padding={20}>
      <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600, marginBottom: 14 }}>Risk profile</h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {/* Portfolio beta needs covariance(holdings, KSE-100) over a rolling
            window — not yet computed. Show a dash + waiting tag rather than
            a fake number. */}
        <RiskRow label="Portfolio beta" value="—" sub="vs KSE-100 · pending" />
        <RiskRow
          label="Concentration"
          value={top ? `${concentration.toFixed(0)}%` : '—'}
          sub={top ? `Top: ${top.sym}` : 'No holdings'}
          warn={concentration > 30}
        />
        <RiskRow
          label="Sector spread"
          value={`${sectorCount} sector${sectorCount === 1 ? '' : 's'}`}
          sub={sectorSpreadSub}
          tone={sectorSpreadTone}
        />
        {/* 30-day vol needs daily portfolio returns — same dependency as the
            equity chart. Once enough history accumulates we'll swap in a
            real stdev computation. */}
        <RiskRow label="Volatility (30d)" value="—" sub="Need 30+ days of data" />
      </div>
    </Card>
  );
};

const RiskRow = ({
  label,
  value,
  sub,
  warn,
  tone,
}: {
  label: string;
  value: string;
  sub: string;
  warn?: boolean;
  tone?: 'up' | 'down';
}) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
    <div>
      <div style={{ fontSize: 12.5, color: 'var(--tk-ink-2)', fontWeight: 500 }}>{label}</div>
      <div style={{ fontSize: 11, color: 'var(--tk-ink-3)', marginTop: 2 }}>{sub}</div>
    </div>
    <div
      className="mono"
      style={{
        fontSize: 16,
        fontWeight: 700,
        color: warn
          ? 'var(--tk-warn)'
          : tone === 'up'
            ? 'var(--tk-up)'
            : tone === 'down'
              ? 'var(--tk-down)'
              : 'var(--tk-ink)',
      }}
    >
      {value}
    </div>
  </div>
);
